import * as THREE from 'three';
import { ARTWORKS } from '../../data/artworks';

// ── Environment ──
export const API = process.env.REACT_APP_BACKEND_URL;

// ── Room dimensions ──
export const ROOM_W = 19;
export const ROOM_H = 10;
export const ROOM_D = 100;

// ── Player ──
export const FOLLOW_DIST   = 4.5;
export const FOLLOW_HEIGHT = 2.85;
export const AVATAR_SPEED  = 5.0;
export const LOOK_SENS     = 0.0028;

// ── Special positions ──
export const GUESTBOOK_Z = -40;
export const PORTAL_Z    = -45;

// ── Helpers ──
export function normalizeAngle(a) {
  while (a > Math.PI)  a -= 2 * Math.PI;
  while (a < -Math.PI) a += 2 * Math.PI;
  return a;
}

// ── Portal rings config ──
export const PORTAL_RINGS = [
  { inner: 3.10, outer: 3.48, color: '#C9A84C', spd:  0.10 },
  { inner: 2.58, outer: 2.80, color: '#F0E9D2', spd: -0.07 },
  { inner: 2.02, outer: 2.20, color: '#D4B460', spd:  0.16 },
  { inner: 1.55, outer: 1.68, color: '#E8D890', spd: -0.12 },
  { inner: 1.10, outer: 1.20, color: '#C9A84C', spd:  0.22 },
  { inner: 0.62, outer: 0.70, color: '#FFF5D8', spd: -0.26 },
];

// ── Daily Spiritual Texts ──
export const DAILY_TEXTS_FR = [
  { quote: "La lumière ne cherche pas à briller.\nElle est. Et le monde autour d'elle s'éveille.", end: "Le voyage commence maintenant." },
  { quote: "Chaque âme porte en elle une étincelle.\nNon pour éclairer les autres — mais pour retrouver son propre foyer.", end: "Entre dans cette lumière." },
  { quote: "Il est dit que D-ieu crée chaque jour de nouveaux mondes.\nAujourd'hui, c'est celui-ci qui s'est ouvert pour toi.", end: "Le voyage commence maintenant." },
  { quote: "La Torah nous enseigne que l'étude transforme celui qui l'accomplit.\nIci, c'est la beauté qui transforme.", end: "Laisse-toi transformer." },
  { quote: "Là où s'arrêtent les mots, commencent les couleurs.\nLà où s'arrêtent les couleurs, commence le silence sacré.", end: "Entre dans ce silence." },
  { quote: "Le Rambam enseignait que la beauté est un chemin\nvers la connaissance de D-ieu.", end: "Le voyage commence maintenant." },
  { quote: "Le monde fut créé pour une seule personne, disent nos Sages.\nAujourd'hui, ce monde est créé pour toi.", end: "Le voyage commence maintenant." },
  { quote: "La harpe de David vibrait d'elle-même au vent du Nord.\nLe vent souffle encore.", end: "Écoute." },
  { quote: "Ce que l'œil voit n'est que la surface de ce qui est.\nCe que le cœur ressent, c'est ce qui est vrai.", end: "Ressens." },
  { quote: "Chaque couleur que tu verras ici a été posée\navec intention et prière. Elle t'attend.", end: "Reçois ce qui t'est destiné." },
  { quote: "Le Baal Shem Tov enseignait :\nD-ieu se cache dans la joie. Cherche-le ici.", end: "Le voyage commence maintenant." },
  { quote: "Rien n'est laissé au hasard dans une vie guidée par la foi.\nTu es là où tu dois être.", end: "Continue." },
  { quote: "La beauté est une fenêtre.\nSi tu regardes assez longtemps à travers, tu verras ce qui se trouve derrière.", end: "Regarde." },
  { quote: "L'art n'imite pas la nature.\nIl révèle ce que la nature dissimule.", end: "Le voyage commence maintenant." },
  { quote: "Nos ancêtres portaient le désert en eux\ncomme une préparation à la lumière.", end: "Avance dans ta traversée." },
  { quote: "Un seul mot peut contenir un monde entier.\nUn seul trait de pinceau aussi.", end: "Le voyage commence maintenant." },
  { quote: "Il existe un monde dans lequel toutes les œuvres sincères\nsont préservées pour l'éternité.", end: "Ce que tu vivras ici ne sera pas perdu." },
  { quote: "Le feu purificateur ne détruit pas l'essence.\nIl dévoile ce qui était caché sous l'impureté.", end: "Laisse le feu faire son œuvre." },
  { quote: "En hébreu, l'âme se dit neshama — souffle.\nTu es un souffle dans l'univers de D-ieu.", end: "Respire." },
  { quote: "Ce n'est pas l'artiste qui peint.\nC'est le Divin qui, à travers l'artiste, se révèle.", end: "Le voyage commence maintenant." },
  { quote: "Chaque être humain porte en lui une lettre de la Torah.\nSans cette lettre, la Torah serait incomplète.", end: "Ta lettre est ici." },
  { quote: "La grâce ne se mérite pas.\nElle descend comme la pluie, sur celui qui est prêt à l'accueillir.", end: "Ouvre-toi." },
  { quote: "L'arbre ardent de Moïse brûlait sans se consumer.\nC'est la nature de tout ce qui est véritablement saint.", end: "Entre dans ce feu." },
  { quote: "Le monde fut créé avec dix paroles.\nL'art se crée avec une seule intention : honorer la vie.", end: "Le voyage commence maintenant." },
  { quote: "Chaque instant est une porte.\nLa plupart passent sans être franchies. Celle-ci t'est ouverte.", end: "Franchis-la." },
  { quote: "La bénédiction n'est pas une récompense.\nC'est une présence. Elle est là, partout, pour qui sait la voir.", end: "Vois." },
  { quote: "La grandeur d'un homme ne se mesure pas à ce qu'il a fait,\nmais à ce qu'il a allumé chez les autres.", end: "Laisse-toi allumer." },
  { quote: "Lorsque le Temple fut détruit, D-ieu pleurait.\nMais sa présence ne disparut jamais. Elle se déplaça.", end: "Elle est ici aussi." },
  { quote: "Créer, c'est participer à l'acte divin de formation du monde.\nChaque œuvre est une continuation de la Création.", end: "Le voyage commence maintenant." },
  { quote: "L'élévation spirituelle ne se décrète pas.\nElle advient, discrètement, lorsque tu t'y prêtes.", end: "Prête-toi à elle maintenant." },
];

export const getDailyText = () => {
  const day = new Date().getDate();
  return DAILY_TEXTS_FR[(day - 1) % DAILY_TEXTS_FR.length];
};

// ── Patch for @emergentbase/visual-edits ──
(function patchThreeForVisualEdits() {
  const SWALLOW = new Proxy(Object.create(null), {
    get(_, k) { return typeof k === 'string' && k !== 'then' ? SWALLOW : undefined; },
    set() { return true; },
    has() { return true; },
  });
  const bases = [THREE.Object3D, THREE.BufferGeometry, THREE.Material, THREE.Fog, THREE.FogExp2, THREE.EventDispatcher].filter(Boolean);
  bases.forEach((Base) => {
    try {
      if (!Base?.prototype) return;
      if (Object.getOwnPropertyDescriptor(Base.prototype, 'x')) return;
      Object.defineProperty(Base.prototype, 'x', { get() { return SWALLOW; }, set() {}, enumerable: false, configurable: true });
    } catch (_) { /* defineProperty non supporté sur ce prototype THREE.js — ignoré sans impact */ }
  });
}());

// ── Preload artwork textures into browser cache ──
THREE.Cache.enabled = true;
ARTWORKS.forEach(aw => {
  if (aw.imageUrl) {
    const img = new Image();
    img.src = aw.imageUrl;
  }
});
